import { useContext, useState } from 'react';
import { AppState } from '../context/general';
import { TextButton } from 't-lib-ui/widgets';
import { useOfflineMode } from '../utils/useOfflineMode';
import './OfflineScreen.css';

// Full-page state shown instead of any app page while the engine is offline (see AppRouter).
// The sidebar keeps rendering next to it, so the user can still browse their feed list; every
// page they pick lands back here until the connection returns.
const OfflineScreen: React.FC = () => {
  const appState = useContext(AppState);
  const mode = useOfflineMode();
  const [retrying, setRetrying] = useState(false);

  // A manual retry does the same clean-slate load the engine retries on its own backoff.
  // On success the engine clears the offline mode and this whole screen is replaced.
  const retry = async () => {
    setRetrying(true);
    try { await appState.feedEngine.reloadData(); }
    finally { setRetrying(false); }
  };

  return (
    <div className="offlineScreen">
      <div className="offlineScreenBadge">
        {/* Wifi symbol struck through: the arcs are concentric around the dot, and the mask
            punches a gap around the slash so it cuts them cleanly instead of just overlapping. */}
        <svg className="offlineScreenIcon" width="72" height="72" viewBox="0 0 24 24" aria-hidden="true">
          <mask id="offlineScreenCut">
            <rect width="24" height="24" fill="#fff" />
            <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" stroke="#000" strokeWidth="5" strokeLinecap="round" />
          </mask>
          {/* Arcs are concentric around the dot at (12, 19.4) — radii 13 / 8.7 / 4.4, each
              spanning ±52°, so the four levels sit an even 4.3 units apart. */}
          <g mask="url(#offlineScreenCut)" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <path d="M1.76 11.39a13 13 0 0 1 20.48 0" />
            <path d="M5.14 14.04a8.7 8.7 0 0 1 13.72 0" />
            <path d="M8.53 16.69a4.4 4.4 0 0 1 6.94 0" />
            <circle cx="12" cy="19.4" r="1.2" fill="currentColor" stroke="none" />
          </g>
          <line x1="3.5" y1="3.5" x2="20.5" y2="20.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        </svg>
      </div>
      <h1>{mode == 'event' ? 'You are offline' : 'Downread is unreachable'}</h1>
      <p>
        {mode == 'event'
          ? 'Your device has no internet connection, so new posts cannot be loaded right now.'
          : 'Your posts cannot be loaded because the Downread server cannot be reached right now.'}
      </p>
      <p className="offlineScreenHint">Downread keeps trying and continues automatically as soon as the connection is back.</p>
      <TextButton label={retrying ? 'Trying…' : 'Try again'} type="major" busy={retrying} onClick={retry} />
    </div>
  );
};

export default OfflineScreen;
